import type { ErrorBody } from './generated/ErrorBody'
import type { ErrorCode } from './generated/ErrorCode'

export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode

  constructor(status: number, code: ErrorCode) {
    super(`API error ${status}: ${code}`)
    this.status = status
    this.code = code
  }
}

export type ErrorMessages = Partial<Record<ErrorCode, string>>

const messages: ErrorMessages = {
  invalid_email: 'Adresse email invalide.',
  invalid_name: 'Nom trop long (200 caractères au plus).',
  invalid_phone: 'Numéro de téléphone invalide.',
  sms_country_unsupported: "Pas d'envoi de SMS vers ce pays : connectez-vous par email.",
  unknown_phone:
    'Aucun compte avec ce numéro. Connectez-vous par email, puis ajoutez votre numéro dans votre profil pour vous connecter aussi par SMS.',
  invalid_code: 'Code incorrect.',
  challenge_expired: 'Ce code ou ce lien a expiré. Demandez-en un nouveau.',
  contact_taken:
    'Cette adresse ou ce numéro sert déjà à un autre compte : connectez-vous avec lui.',
  rate_limited: 'Trop de demandes. Réessayez dans quelques minutes.',
  forbidden: 'Action réservée aux organisateurs.',
  not_found: "Introuvable, ou vous n'y avez pas accès.",
  invalid_event: "Paramètres de l'événement invalides.",
  slug_taken: 'Cette adresse est déjà prise par un autre événement.',
  invalid_position: 'Poste invalide.',
  position_name_taken: 'Un poste porte déjà ce nom dans cet événement.',
  invalid_shift: 'Créneau invalide : la fin doit suivre le début, avec 1 à 1000 bénévoles.',
  shift_has_assignments: 'Ce créneau a déjà des bénévoles : il ne peut plus être découpé.',
  invalid_availability: 'Disponibilités invalides : chaque plage doit finir après son début.',
  invalid_push_subscription: 'Ce navigateur ne permet pas de recevoir les notifications.',
  volunteer_unavailable: "Ce bénévole n'est plus disponible.",
  withdrawal_closed: 'Le délai de désistement est passé : écrivez aux organisateurs.',
  invalid_message: 'Message vide ou trop long.',
  invalid_photo: 'Photo illisible ou trop lourde : choisissez une image JPEG, PNG ou WebP.',
  invalid_media:
    'Fichier refusé : vidéo MP4, MOV ou WebM de 100 Mo au plus, ou audio de 20 Mo au plus.',
  invalid_group: 'Groupe invalide.',
  invalid_question: 'Question invalide.',
  invalid_answers:
    'Réponses manquantes ou invalides. Rechargez la page si les questions ont changé.',
  invalid_passkey: "Clé d'accès non reconnue. Connectez-vous par email.",
  google_login_failed: 'Connexion Google impossible. Connectez-vous par email.',
  google_email_unproven: 'Google ne garantit pas cette adresse : connectez-vous par email.',
  reauthentication_required:
    "Pour ajouter une clé d'accès, déconnectez-vous puis reconnectez-vous par email.",
}

/** The user-facing message of an error; `overrides` adapt some codes to their context. */
export function errorMessage(error: unknown, overrides?: ErrorMessages): string {
  const message =
    error instanceof ApiError ? (overrides?.[error.code] ?? messages[error.code]) : undefined
  return message ?? 'Une erreur est survenue. Réessayez.'
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return send<T>(
    method,
    path,
    body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  )
}

async function send<T>(method: string, path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, { ...init, method, credentials: 'same-origin' })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ErrorBody | null
    throw new ApiError(response.status, payload?.error ?? 'internal')
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
  /** POSTs a file as the raw body, with its own content type. */
  upload: <T>(path: string, file: Blob) =>
    send<T>('POST', path, { headers: { 'Content-Type': file.type }, body: file }),
}
